import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  GitCommit,
  Loader2,
  AlertCircle,
  Check,
  RotateCcw,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  RefreshCw,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Modal } from '@/components/ui/modal';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import axios from 'axios';
import { auth } from '@/lib/firebase';
import { deploymentsApi } from '@/lib/api/deployments';
import { Deployment, DeployStatus } from '@/types/deployment';
import toast from 'react-hot-toast';
import { usePermissions } from '@/hooks/usePermissions';

const API_URL = import.meta.env.VITE_API_URL;

interface Commit {
  id: string;
  commitSha: string;
  message: string;
  branch: string;
  createdAt: string;
  commitDate: string;
  createdBy?: string;
  fileCount?: number;
  totalSize?: number;
  status: 'creating' | 'uploaded' | 'failed';
  metadata?: {
    changedFiles?: number;
    changedSize?: number;
    [key: string]: any;
  };
}

type TimelineItem =
  | { type: 'commit'; data: Commit }
  | { type: 'deployment'; data: Deployment };

interface CommitHistoryProps {
  devContainerId: string;
  projectId?: string;
  isStreaming?: boolean;
  className?: string;
  hideHeader?: boolean;
  onBack?: () => void;
}

export function CommitHistory({
  devContainerId,
  projectId,
  isStreaming = false,
  className,
  hideHeader = false,
  onBack,
}: CommitHistoryProps) {
  const { canCommit, canDeploy } = usePermissions();
  const [commits, setCommits] = useState<Commit[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [currentDeployment, setCurrentDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatingCommit, setCreatingCommit] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [showCommitForm, setShowCommitForm] = useState(false);
  const [rollingBack, setRollingBack] = useState<string | null>(null);
  const [deploymentToRollback, setDeploymentToRollback] = useState<{ id: string; deployNumber: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load commits and deployments on component mount
  useEffect(() => {
    if (!loading && commits.length === 0) {
      loadHistory();
    }
  }, []);

  const loadHistory = async () => {
    if (!projectId) {
      console.log('No projectId available, skipping history load');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get auth token
      const token = await auth.getIdToken();

      // Load both commits and deployments in parallel
      const [commitsResponse, deploymentsData, currentData] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${projectId}/commits`, {
          params: { limit: 50 },
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        }),
        deploymentsApi.getDeployments(projectId, 50, 'publish'),  // Only fetch publish deployments
        deploymentsApi.getCurrentDeployment(projectId),
      ]);

      const commitsData = Array.isArray(commitsResponse.data) ? commitsResponse.data : [];
      setCommits(commitsData);
      setDeployments(deploymentsData);
      setCurrentDeployment(currentData);
    } catch (err: any) {
      // Handle 404 differently - it might just mean no commits yet
      if (err.response?.status === 404) {
        setCommits([]);
        setDeployments([]);
        setError(null);
      } else {
        const errorMessage = err.response?.data?.message ||
                            err.response?.data?.error ||
                            err.message ||
                            'Failed to load version history';
        setError(errorMessage);
        console.error('Error loading history:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const createCommit = async () => {
    if (!commitMessage.trim() || !projectId) return;

    setCreatingCommit(true);
    setError(null);

    try {
      const token = await auth.getIdToken();

      const response = await axios.post(
        `${API_URL}/api/projects/${projectId}/commits`,
        { message: commitMessage },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );

      const currentCommits = Array.isArray(commits) ? commits : [];
      setCommits([response.data, ...currentCommits]);
      setCommitMessage('');
      setShowCommitForm(false);

      toast.success('Commit created successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          err.message ||
                          'Failed to create commit';

      if (errorMessage === 'No changes to commit') {
        // Show info toast instead of error for no changes
        toast('No changes to commit. Make some changes to your files first.', {
          icon: 'ℹ️',
        });
        setShowCommitForm(false);
        setCommitMessage('');
      } else {
        setError(errorMessage);
        console.error('Error creating commit:', err);
      }
    } finally {
      setCreatingCommit(false);
    }
  };

  const openRollbackModal = (deploymentId: string, deployNumber: number) => {
    setDeploymentToRollback({ id: deploymentId, deployNumber });
  };

  const handleRollback = async () => {
    if (!deploymentToRollback) return;

    setRollingBack(deploymentToRollback.id);
    setDeploymentToRollback(null);

    try {
      await deploymentsApi.rollbackToDeployment(projectId!, deploymentToRollback.id);
      toast.success(`Rolled back to deployment #${deploymentToRollback.deployNumber}`);
      await loadHistory();
    } catch (error: any) {
      console.error('Rollback failed:', error);
      toast.error(error?.message || 'Failed to rollback deployment');
    } finally {
      setRollingBack(null);
    }
  };

  // Deduplicate deployments by buildId - show only the latest deployment per build
  // This handles cases where visibility changes trigger republish of the same build
  const deduplicatedDeployments = (() => {
    const buildMap = new Map<string, Deployment>();
    // deployments are already sorted by createdAt DESC from API
    // For each buildId, keep only the first (latest) deployment
    for (const deployment of deployments) {
      if (!buildMap.has(deployment.buildId)) {
        buildMap.set(deployment.buildId, deployment);
      }
    }
    return Array.from(buildMap.values());
  })();

  // Merge commits and deployments into a unified timeline
  const timeline: TimelineItem[] = [
    ...commits.map((commit): TimelineItem => ({ type: 'commit', data: commit })),
    ...deduplicatedDeployments.map((deployment): TimelineItem => ({ type: 'deployment', data: deployment })),
  ].sort((a, b) => {
    const dateA = a.type === 'commit' ? new Date(a.data.createdAt) : new Date(a.data.createdAt);
    const dateB = b.type === 'commit' ? new Date(b.data.createdAt) : new Date(b.data.createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return format(date, 'h:mm a');
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else if (diffInHours < 168) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  const formatFileSize = (bytes: number | bigint) => {
    if (bytes === 0) return '0 B';
    const num = typeof bytes === 'bigint' ? Number(bytes) : bytes;
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(num) / Math.log(k));
    return parseFloat((num / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getDeploymentStatusIcon = (status: DeployStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
      case 'timeout':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'running':
      case 'queued':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <ClockIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const getDeploymentStatusBadge = (status: DeployStatus) => {
    const config = {
      success: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
      failed: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
      running: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
      queued: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300',
      timeout: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-950 dark:text-gray-300',
    };

    return (
      <Badge className={cn('text-[11px] px-2 py-0.4 font-medium rounded-sm', config[status])}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className={cn("h-full flex flex-col bg-background", className)}>
      {/* Header */}
      {!hideHeader && (
        <div className="h-12 px-4 border-b border-border flex items-center justify-between bg-background">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-muted-foreground transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Version History</span>
          </button>
        </div>
      )}

      {/* New Commit Button */}
      <div className="px-3 py-2 border-b border-border bg-card">
        {!showCommitForm && devContainerId && (
          <div className="flex gap-2">
            {canCommit && (
              <Button
                size="sm"
                variant="default"
                onClick={() => {
                  setShowCommitForm(true);
                  setTimeout(() => inputRef.current?.focus(), 100);
                }}
                className="flex-1 h-8 gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-xs rounded-sm"
                disabled={creatingCommit || isStreaming}
              >
                New Commit
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={loadHistory}
              className={cn("h-8 px-2.5 rounded-sm", !canCommit && "flex-1")}
              disabled={loading}
              title="Refresh history"
            >
              <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
            </Button>
          </div>
        )}

        {/* Commit creation form */}
        {showCommitForm && canCommit && (
          <div className="flex gap-2 items-center">
            <input
              ref={inputRef}
              type="text"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !creatingCommit) {
                  createCommit();
                } else if (e.key === 'Escape') {
                  setShowCommitForm(false);
                  setCommitMessage('');
                }
              }}
              placeholder="Commit message..."
              className="flex-1 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              disabled={creatingCommit}
            />
            <Button
              size="sm"
              onClick={createCommit}
              disabled={!commitMessage.trim() || creatingCommit}
              className="h-7 px-3 text-xs bg-primary hover:bg-primary/90 rounded-sm"
            >
              {creatingCommit ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Check className="w-3 h-3" />
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowCommitForm(false);
                setCommitMessage('');
              }}
              className="h-7 px-3 text-xs rounded-sm"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Timeline */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <AlertCircle className="w-6 h-6 text-destructive mb-2" />
            <p className="text-sm text-muted-foreground text-center">{error}</p>
            {!error.includes('Power mode') && !error.includes('dev container') && (
              <Button
                size="sm"
                variant="ghost"
                onClick={loadHistory}
                className="mt-2 text-xs"
              >
                Retry
              </Button>
            )}
          </div>
        ) : timeline.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <GitCommit className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground text-center">
              {!devContainerId ? 'Version history requires Power mode' : 'No commits or deployments yet'}
            </p>
            <p className="text-xs text-muted-foreground/80 text-center mt-1">
              {!devContainerId
                ? 'Switch to Power mode to enable version control'
                : 'Create your first commit to get started'}
            </p>
          </div>
        ) : (
          <div className="px-2 py-2 space-y-2">
            {timeline.map((item) => {
              if (item.type === 'commit') {
                const commit = item.data;
                return (
                  <div
                    key={`commit-${commit.id}`}
                    className="w-full p-3 hover:bg-muted/50 transition-colors rounded-sm"
                  >
                    <div className="flex items-start gap-3">
                      <GitCommit className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0 space-y-1">
                        <p className="text-sm text-foreground break-words leading-relaxed">
                          {commit.message}
                        </p>
                        <div className="flex items-center gap-2.5 flex-wrap text-xs text-muted-foreground">
                          <span className="font-medium">
                            {formatTime(commit.commitDate || commit.createdAt)}
                          </span>
                          {commit.branch && (
                            <>
                              <span className="text-muted-foreground/40">•</span>
                              <span className="font-mono">{commit.branch}</span>
                            </>
                          )}
                          {commit.metadata?.changedFiles !== undefined && (
                            <>
                              <span className="text-muted-foreground/40">•</span>
                              <span>{commit.metadata.changedFiles} {commit.metadata.changedFiles === 1 ? 'file' : 'files'}</span>
                            </>
                          )}
                          {commit.metadata?.changedSize !== undefined && commit.metadata.changedSize > 0 && (
                            <>
                              <span className="text-muted-foreground/40">•</span>
                              <span className="text-green-600 dark:text-green-400">
                                +{commit.metadata.changedSize} lines
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else {
                const deployment = item.data;
                const isCurrent = currentDeployment?.id === deployment.id;
                const canRollback = canDeploy && !isCurrent && deployment.status === 'success' && rollingBack !== deployment.id;

                return (
                  <div
                    key={`deployment-${deployment.id}`}
                    className={cn(
                      "w-full p-3 rounded-sm transition-colors",
                      isCurrent
                        ? "bg-muted/50 ring-1 ring-border"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0">
                        {getDeploymentStatusIcon(deployment.status)}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-foreground">
                              Deploy #{deployment.deployNumber}
                            </span>
                            {getDeploymentStatusBadge(deployment.status)}
                          </div>
                          <div className="flex items-center gap-2">
                            {isCurrent && (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 text-[11px] px-2.5 py-0.5 font-medium">
                                Live
                              </Badge>
                            )}
                            {canRollback && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRollbackModal(deployment.id, deployment.deployNumber)}
                                disabled={rollingBack !== null}
                                className="h-7 px-2 text-xs hover:bg-muted"
                              >
                                {rollingBack === deployment.id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                                    Reverting...
                                  </>
                                ) : (
                                  <>
                                    <RotateCcw className="w-3 h-3 mr-1.5" />
                                    Revert
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 flex-wrap text-xs text-muted-foreground">
                          <span className="font-medium">
                            {formatTime(typeof deployment.createdAt === 'string' ? deployment.createdAt : deployment.createdAt.toISOString())}
                          </span>
                          {deployment.fileCount !== undefined && deployment.fileCount > 0 && (
                            <>
                              <span className="text-muted-foreground/40">•</span>
                              <span>{deployment.fileCount} {deployment.fileCount === 1 ? 'file' : 'files'}</span>
                            </>
                          )}
                          {deployment.totalSize !== undefined && deployment.totalSize > 0 && (
                            <>
                              <span className="text-muted-foreground/40">•</span>
                              <span>{formatFileSize(deployment.totalSize)}</span>
                            </>
                          )}
                          {deployment.duration !== undefined && deployment.duration !== null && (
                            <>
                              <span className="text-muted-foreground/40">•</span>
                              <span>{deployment.duration === 0 ? '< 1s' : `${deployment.duration}s`}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </ScrollArea>

      {/* Rollback Confirmation Modal */}
      {deploymentToRollback && (
        <Modal
          isOpen={!!deploymentToRollback}
          onClose={() => setDeploymentToRollback(null)}
          title="Confirm Rollback"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to rollback to deployment #{deploymentToRollback.deployNumber}?
            </p>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p>This will:</p>
              <ul className="space-y-1.5 ml-1">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Switch your live app to this deployment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Reset your editor code to that commit</span>
                </li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeploymentToRollback(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRollback}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Confirm Rollback
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
